import { Button } from '@components/Button/Button';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useLazyGetFreeReportQuery, useLazyGetTaskReportQuery } from '@/api/report/api';
import { ReportDate } from '@/pages/Report/components/ReportDate/ReportDate';
import { ReportTable } from '@/pages/Report/components/ReportTable/ReportTable';
import { freeReportLevel } from '@/pages/Report/routes.config';
import { FreeReportFilter, TaskReportFilter } from '@/pages/Report/types';
import { Sort } from '@/shared/api/types';
import { ReportType } from '@/shared/report/enum';
import { Auth, hasPermission } from '@/shared/security/hasPermission';
import { report } from '@/shared/security/permissions/report';
import { withSecurity } from '@/shared/security/withSecurity';

interface ReportComponentProps {
  type: ReportType;
  auth: Auth;
}

const isTaskFilter = (
  filter: TaskReportFilter | FreeReportFilter,
  type: ReportType,
): filter is TaskReportFilter => {
  return type === ReportType.TASK && 'organizationId' in filter;
};

export const ReportComponent = withSecurity(({ type, auth }: ReportComponentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationId = searchParams.get('organizationId');

  const [dateAt, setDateAt] = useState<Dayjs | [Dayjs, Dayjs] | null>(() => {
    const dateAtParam = searchParams.get('dateAt');
    const dateAtRangeParam =
      searchParams.get('dateAtFrom') && searchParams.get('dateAtTo')
        ? [searchParams.get('dateAtFrom'), searchParams.get('dateAtTo')]
        : null;
    return dateAtParam
      ? dayjs(dateAtParam)
      : dateAtRangeParam
        ? [dayjs(dateAtRangeParam[0]), dayjs(dateAtRangeParam[1])]
        : null;
  });
  const [currentSort, setCurrentSort] = useState<Sort[] | undefined>();

  const changeDateAtParam = (dateAt: Dayjs | [Dayjs, Dayjs] | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (dateAt) {
      if (type === ReportType.TASK) {
        const [from, to] = (dateAt ?? []) as [Dayjs, Dayjs];
        newParams.set('dateAtFrom', from.format('YYYY-MM-DD'));
        newParams.set('dateAtTo', to.format('YYYY-MM-DD'));
      } else {
        newParams.set('dateAt', (dateAt as Dayjs).format('YYYY-MM-DD'));
      }
    } else {
      newParams.delete('dateAt');
      newParams.delete('dateAtFrom');
      newParams.delete('dateAtTo');
    }
    setSearchParams(newParams);
  };

  const filters = useMemo(() => {
    if (type === ReportType.TASK) {
      const [from, to] = (dateAt ?? []) as [Dayjs, Dayjs];
      return {
        dateAtFrom: from?.format('YYYY-MM-DD') || '',
        dateAtTo: to?.format('YYYY-MM-DD') || '',
        organizationId: organizationId || '',
      } as TaskReportFilter;
    }
    return { dateAt: (dateAt as Dayjs)?.format('YYYY-MM-DD') || '' } as FreeReportFilter;
  }, [type, dateAt, organizationId]);

  const [getTaskReport, taskReportResult] = useLazyGetTaskReportQuery();
  const [getFreeReport, freeReportResult] = useLazyGetFreeReportQuery();

  const isTaskType = type === ReportType.TASK;
  const isReadyToGenerate = dateAt && (isTaskType ? Boolean(organizationId) : true);

  const handleChangeDate = useCallback(
    (value: Dayjs | [Dayjs, Dayjs] | null) => {
      setDateAt(value);
      changeDateAtParam(value);
      setCurrentSort(undefined);
      taskReportResult.reset();
      freeReportResult.reset();
    },
    [searchParams],
  );

  const handleLoadReport = useCallback(
    (sort?: Sort[]) => {
      if (!dateAt) return;

      setCurrentSort(sort);

      if (isTaskType && isTaskFilter(filters, type)) {
        getTaskReport({ filters, sort });
      } else if (!isTaskType) {
        getFreeReport({ filters, sort });
      }
    },
    [isTaskType, dateAt, getTaskReport, getFreeReport, filters],
  );

  const { data, isFetching, isSuccess } =
    type === ReportType.TASK ? taskReportResult : freeReportResult;

  const showTable = dateAt && isSuccess;

  useEffect(() => {
    taskReportResult.reset();
    freeReportResult.reset();
  }, [organizationId]);

  const displayBtn = useMemo(() => {
    const permitsConfig =
      type === ReportType.TASK
        ? { permissions: report.taskReport.create }
        : {
            permissions: report.summaryReport.create,
            level: freeReportLevel,
          };
    return hasPermission(auth, permitsConfig);
  }, [auth, type]);

  useEffect(() => {
    if (dateAt && isReadyToGenerate && !taskReportResult.data && isTaskType) {
      handleLoadReport();
    }
  }, []);

  return (
    <>
      <ReportDate
        isRange={type === ReportType.TASK}
        onChange={handleChangeDate}
        value={dateAt}
      />
      <Button
        text="Сформировать отчет"
        type="primary"
        style={{ width: 200, marginTop: 15, marginBottom: 50 }}
        action={() => handleLoadReport()}
        loading={isFetching}
        disabled={!isReadyToGenerate}
        display={displayBtn}
      />
      {dateAt && showTable && (
        <ReportTable
          type={type}
          dateAt={dateAt}
          data={data}
          filters={filters}
          currentSort={currentSort}
          onChange={handleLoadReport}
        />
      )}
    </>
  );
});
